import * as React from 'react';
import { useSelector } from 'react-redux';

import { featureTreeReducerName } from '../Redux/FeatureTree/reducers';
import type { IFeatureTreeState } from '../Redux/FeatureTree/types';
import type { IFeatureNode } from '../Redux/FeatureTree/action-types';

/** Walks the keyed feature tree by capability path, e.g. 'page.hrcDashboard/field.ssn'. */
function findNode(features: Record<string, IFeatureNode> | undefined, key: string): IFeatureNode | undefined {
  if (!features) return undefined;
  const segments = key.split('/').filter(Boolean);
  let map: Record<string, IFeatureNode> = features;
  let node: IFeatureNode | undefined;
  for (const seg of segments) {
    node = map[seg];
    if (!node) return undefined;
    map = node.children ?? {};
  }
  return node;
}

/**
 * Resolves a capability key to its access. Defaults to hidden (accessLevel 0) until the tree
 * loads, so unauthorized content never flashes. Pass keys like 'page.hrcDashboard' or
 * 'page.hrcDashboard/field.ssn' for nested nodes.
 */
export function useFeature(key: string): { enabled: boolean; readOnly: boolean; accessLevel: number; isSensitive: boolean } {
  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) => s?.dynamic?.[featureTreeReducerName] as IFeatureTreeState | undefined
  );
  const node = findNode(state?.tree?.features, key);
  const accessLevel = node?.accessLevel ?? 0;
  return { enabled: accessLevel > 0, readOnly: accessLevel === 1, accessLevel, isSensitive: node?.isSensitive ?? false };
}

/** Returns true while the tree is still loading, so callers can avoid premature redirects. */
export function useFeatureTreeLoading(): boolean {
  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) => s?.dynamic?.[featureTreeReducerName] as IFeatureTreeState | undefined
  );
  return !state || state.isLoading || (!state.tree && !state.hasError);
}

/**
 * Resolves a fine-grained API permission (e.g. 'Cases.Pause', 'Validation.Proceed') from the
 * feature-config `permissions` array — the same keys enforced server-side via [AuthorizedRole].
 * Use to hide/disable workflow action buttons so the UI matches the API's 403 boundary.
 * Defaults to false until the tree loads.
 */
export function usePermission(permissionKey: string): boolean {
  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) => s?.dynamic?.[featureTreeReducerName] as IFeatureTreeState | undefined
  );
  const permissions = state?.tree?.permissions;
  if (!permissions) return false;
  return permissions.some((p) => p.toLowerCase() === permissionKey.toLowerCase());
}

/**
 * Resolves the current PBT role from feature-config ('Manager' | 'HRC' | 'HRCLeader' | 'Admin'),
 * or null until the tree loads. Use to drive role-specific UI (e.g. attest button labels).
 */
export function useRole(): string | null {
  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) => s?.dynamic?.[featureTreeReducerName] as IFeatureTreeState | undefined
  );
  return state?.tree?.role ?? null;
}

/**
 * Returns the enabled child capabilities under a key, each as { key, node }. Use to build
 * data-driven columns/tabs/menus straight from the resolved tree — no hardcoded gating.
 */
export function useFeatureChildren(parentKey: string): Array<{ key: string; node: IFeatureNode }> {
  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) => s?.dynamic?.[featureTreeReducerName] as IFeatureTreeState | undefined
  );
  const parent = findNode(state?.tree?.features, parentKey);
  const children = parent?.children;
  if (!children) return [];
  return Object.entries(children)
    .filter(([, n]) => n.accessLevel > 0)
    .map(([key, node]) => ({ key, node }));
}

/** Renders children only when the capability is enabled; otherwise nothing. */
export function Feature({ name, children }: { name: string; children: React.ReactNode }): React.ReactElement | null {
  const { enabled } = useFeature(name);
  return enabled ? <>{children}</> : null;
}
